import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ConfirmationModal = ({ isOpen, onClose, appointmentDetails, emailError }) => {
  if (!isOpen) return null;

  const { supportType, slot, formData } = appointmentDetails;

  const supportTypeLabels = {
    strategy: 'Strategy Consultation',
    content: 'Content Planning',
    technical: 'Technical Support',
    training: 'Training Session'
  };

  const handleAddToCalendar = () => {
    try {
      // Parse the date and time to create proper calendar event
      const eventDate = new Date(slot?.date);
      const [time, period] = slot?.displayTime?.split(' ');
      const [hours, minutes] = time?.split(':');
      let hour = parseInt(hours);
      
      if (period === 'PM' && hour !== 12) {
        hour += 12;
      } else if (period === 'AM' && hour === 12) {
        hour = 0;
      }
      
      eventDate?.setHours(hour, parseInt(minutes || 0), 0, 0);
      
      // Create end time (1 hour later)
      const endDate = new Date(eventDate);
      endDate?.setHours(endDate?.getHours() + 1);
      
      // Format dates for iCal format (YYYYMMDDTHHMMSS)
      const formatICalDate = (date) => {
        const pad = (n) => n?.toString()?.padStart(2, '0');
        return `${date?.getFullYear()}${pad(date?.getMonth() + 1)}${pad(date?.getDate())}T${pad(date?.getHours())}${pad(date?.getMinutes())}${pad(date?.getSeconds())}`;
      };
      
      const startTime = formatICalDate(eventDate);
      const endTime = formatICalDate(endDate);
      
      // Create iCal content
      const icsContent = [
        'BEGIN:VCALENDAR',
        'VERSION:2.0',
        'PRODID:-//Videra Surgical//Marketing Support//EN',
        'CALSCALE:GREGORIAN',
        'METHOD:PUBLISH',
        'BEGIN:VEVENT',
        `DTSTART:${startTime}`,
        `DTEND:${endTime}`,
        `DTSTAMP:${formatICalDate(new Date())}`,
        `SUMMARY:${supportTypeLabels?.[supportType]} - Videra Surgical`,
        `DESCRIPTION:Marketing support consultation with Videra Surgical.\n\nProvider: ${formData?.fullName}\nEmail: ${formData?.email}\nPhone: ${formData?.phone}\nPractice: ${formData?.practiceName}`,
        `LOCATION:Virtual Meeting`,
        `STATUS:CONFIRMED`,
        `SEQUENCE:0`,
        `BEGIN:VALARM`,
        `TRIGGER:-PT24H`,
        `ACTION:DISPLAY`,
        `DESCRIPTION:Reminder: ${supportTypeLabels?.[supportType]} appointment tomorrow`,
        `END:VALARM`,
        'END:VEVENT',
        'END:VCALENDAR'
      ]?.join('\r\n');
      
      // Create blob and download
      const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `videra-marketing-support-${slot?.date}.ics`;
      document.body?.appendChild(link);
      link?.click();
      document.body?.removeChild(link);
      URL.revokeObjectURL(link?.href);
    } catch (error) {
      console.error('Error creating calendar event:', error);
      alert('Unable to create calendar event. Please try again.');
    }
  };

  const handleDownloadConfirmation = () => {
    try {
      const selectedPlatforms = Object.entries(formData?.platforms || {})
        ?.filter(([_, value]) => value)
        ?.map(([key]) => key?.charAt(0)?.toUpperCase() + key?.slice(1))
        ?.join(', ') || 'None specified';
      
      const confirmationHtml = `
        <!DOCTYPE html>
        <html>
          <head>
            <meta charset="UTF-8">
            <title>Appointment Confirmation</title>
            <style>
              body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 800px; margin: 0 auto; padding: 20px; }
              .header { background-color: #000; color: white; padding: 30px; text-align: center; margin-bottom: 30px; }
              .header h1 { margin: 0; font-size: 28px; }
              .section { margin-bottom: 25px; padding: 20px; background-color: #f9f9f9; border-left: 4px solid #000; }
              .section h2 { margin-top: 0; color: #000; font-size: 20px; }
              .detail { margin: 10px 0; }
              .label { font-weight: bold; color: #000; display: inline-block; width: 150px; }
              .value { color: #333; }
              .footer { text-align: center; padding: 20px; color: #666; font-size: 12px; border-top: 1px solid #ddd; margin-top: 30px; }
              .status { background-color: #d4edda; border: 1px solid #c3e6cb; color: #155724; padding: 15px; border-radius: 5px; margin-bottom: 20px; }
              @media print { body { padding: 0; } }
            </style>
          </head>
          <body>
            <div class="header">
              <h1>Appointment Confirmation</h1>
              <p style="margin: 10px 0 0 0;">Videra Surgical - Marketing Support</p>
            </div>
            
            <div class="status">
              <strong>✓ Confirmed</strong> - Your appointment has been successfully scheduled
            </div>
            
            <div class="section">
              <h2>Appointment Details</h2>
              <div class="detail"><span class="label">Support Type:</span><span class="value">${supportTypeLabels?.[supportType]}</span></div>
              <div class="detail"><span class="label">Date:</span><span class="value">${slot?.fullDate}</span></div>
              <div class="detail"><span class="label">Time:</span><span class="value">${slot?.displayTime} ET</span></div>
              <div class="detail"><span class="label">Duration:</span><span class="value">60 minutes</span></div>
            </div>
            
            <div class="section">
              <h2>Provider Information</h2>
              <div class="detail"><span class="label">Name:</span><span class="value">${formData?.fullName}</span></div>
              <div class="detail"><span class="label">Email:</span><span class="value">${formData?.email}</span></div>
              <div class="detail"><span class="label">Phone:</span><span class="value">${formData?.phone}</span></div>
              <div class="detail"><span class="label">Role:</span><span class="value">${formData?.role}</span></div>
              <div class="detail"><span class="label">Practice:</span><span class="value">${formData?.practiceName}</span></div>
              <div class="detail"><span class="label">Location:</span><span class="value">${formData?.city}, ${formData?.state}</span></div>
            </div>
            
            <div class="section">
              <h2>Consultation Focus</h2>
              <div class="detail"><span class="label">Marketing Goal:</span><span class="value">${formData?.marketingGoal}</span></div>
              <div class="detail"><span class="label">Current Effort:</span><span class="value">${formData?.currentEffort}</span></div>
              <div class="detail"><span class="label">Platforms:</span><span class="value">${selectedPlatforms}</span></div>
              <div class="detail"><span class="label">Challenges:</span><span class="value">${formData?.challenges}</span></div>
              ${formData?.website ? `<div class="detail"><span class="label">Website:</span><span class="value">${formData?.website}</span></div>` : ''}
              ${formData?.additionalNotes ? `<div class="detail"><span class="label">Additional Notes:</span><span class="value">${formData?.additionalNotes}</span></div>` : ''}
            </div>
            
            <div class="section">
              <h2>What Happens Next</h2>
              <ul>
                <li>You will receive a confirmation email at ${formData?.email}</li>
                <li>A calendar invite with the meeting link will be sent</li>
                <li>You'll receive a reminder email 24 hours before the appointment</li>
                <li>Preparation materials will be sent 48 hours in advance</li>
              </ul>
            </div>
            
            <div class="section">
              <h2>Before Your Appointment</h2>
              <ul>
                <li>Review any preparation materials sent via email</li>
                <li>Prepare specific questions or topics to discuss</li>
                <li>Test your video conferencing setup</li>
                <li>Have your current marketing materials ready for review</li>
              </ul>
            </div>
            
            <div class="footer">
              <p><strong>Videra Surgical Providers Portal</strong></p>
              <p>For questions or changes, contact: support@viderasurgical.com | (555) 123-4567</p>
              <p>Confirmation generated on ${new Date()?.toLocaleString('en-US', { timeZone: 'America/New_York' })} ET</p>
            </div>
          </body>
        </html>
      `;
      
      // Create blob and download
      const blob = new Blob([confirmationHtml], { type: 'text/html' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `videra-appointment-confirmation-${slot?.date}.html`;
      document.body?.appendChild(link);
      link?.click();
      document.body?.removeChild(link);
      URL.revokeObjectURL(link?.href);
    } catch (error) {
      console.error('Error creating confirmation download:', error);
      alert('Unable to download confirmation. Please try again.');
    }
  };

  return (
    <div className="fixed inset-0 z-200 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 md:p-8">
          <div className="text-center mb-6">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-success/10 flex items-center justify-center">
              <Icon name="CheckCircle" size={40} color="var(--color-success)" />
            </div>
            <h2 className="text-2xl md:text-3xl font-semibold text-foreground mb-2">
              Appointment Confirmed!
            </h2>
            <p className="text-sm md:text-base text-muted-foreground">
              Your marketing support consultation has been successfully scheduled
            </p>
          </div>

          <div className="space-y-6">
            <div className="bg-muted/50 rounded-lg p-4 md:p-6">
              <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                <Icon name="Calendar" size={20} color="var(--color-primary)" />
                Appointment Details
              </h3>

              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <Icon name="User" size={18} color="var(--color-muted-foreground)" className="flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-foreground">{formData?.fullName}</p>
                    <p className="text-xs text-muted-foreground font-caption">{formData?.email}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Icon name="Building" size={18} color="var(--color-muted-foreground)" className="flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-foreground">{formData?.practiceName}</p>
                    <p className="text-xs text-muted-foreground font-caption">
                      {formData?.city}, {formData?.state}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Icon name="Target" size={18} color="var(--color-muted-foreground)" className="flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      {supportTypeLabels?.[supportType]}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Icon name="Clock" size={18} color="var(--color-muted-foreground)" className="flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-foreground">{slot?.fullDate}</p>
                    <p className="text-xs text-muted-foreground font-caption">
                      {slot?.displayTime} ET
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {emailError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Icon name="AlertCircle" size={18} color="#ef4444" className="flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-red-800">
                    <p className="font-medium mb-1">Error:</p>
                    <p>{emailError}</p>
                  </div>
                </div>
              </div>
            )}

            {!emailError && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Icon name="CheckCircle" size={18} color="#10b981" className="flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-green-800">
                    <p className="font-medium mb-1">Success!</p>
                    <p>Your appointment has been confirmed and saved. Our team will contact you shortly with meeting details.</p>
                  </div>
                </div>
              </div>
            )}

            <div className="border rounded-lg p-4 md:p-6" style={{ backgroundColor: 'var(--corvid-background-color, rgba(var(--bg, var(--color_17, 37, 99, 235)), var(--alpha-bg, 0.05)))', borderColor: 'var(--corvid-background-color, rgba(var(--bg, var(--color_17, 37, 99, 235)), var(--alpha-bg, 0.2)))' }}>
              <h4 className="text-base font-semibold text-foreground mb-3 flex items-center gap-2">
                <Icon name="Mail" size={18} color="black" />
                What Happens Next?
              </h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <Icon name="Check" size={16} color="var(--color-success)" className="flex-shrink-0 mt-0.5" />
                  <span>Our team will send confirmation email to {formData?.email}</span>
                </li>
                <li className="flex items-start gap-2">
                  <Icon name="Check" size={16} color="var(--color-success)" className="flex-shrink-0 mt-0.5" />
                  <span>You'll receive a calendar invite with meeting link</span>
                </li>
                <li className="flex items-start gap-2">
                  <Icon name="Check" size={16} color="var(--color-success)" className="flex-shrink-0 mt-0.5" />
                  <span>Reminder email will be sent 24 hours before appointment</span>
                </li>
                <li className="flex items-start gap-2">
                  <Icon name="Check" size={16} color="var(--color-success)" className="flex-shrink-0 mt-0.5" />
                  <span>Preparation materials will be sent 48 hours in advance</span>
                </li>
              </ul>
            </div>

            <div className="bg-muted/50 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Icon name="Info" size={18} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
                <div className="text-sm text-muted-foreground">
                  <p className="mb-2">
                    <strong className="text-foreground">Before Your Appointment:</strong>
                  </p>
                  <ul className="space-y-1 list-disc list-inside">
                    <li>Review any preparation materials sent via email</li>
                    <li>Prepare specific questions or topics to discuss</li>
                    <li>Test your video conferencing setup</li>
                    <li>Have your current marketing materials ready for review</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                variant="outline"
                onClick={handleAddToCalendar}
                iconName="Calendar"
                iconPosition="left"
                className="flex-1"
              >
                Add to Calendar
              </Button>

              <Button
                variant="outline"
                onClick={handleDownloadConfirmation}
                iconName="Download"
                iconPosition="left"
                className="flex-1"
              >
                Download Confirmation
              </Button>
            </div>

            <Button variant="default" onClick={onClose} fullWidth>
              Return to Dashboard
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal;